<template>
  <div>


    <div class="d-flex flex-row justify-content-around"
      style="padding-top: 0%; position: relative; overflow: hidden; display: flex; justify-content: center; align-items: center; height:80vh">
      <!-- Square chart when !showCountry -->
      <div v-if="!showCountry" :id="chartId"
        style="top: 0; height: 97%; width: 97%; max-height: 100%; max-width:60%; position: absolute; overflow: hidden; margin-top: 30px">
      </div>

      <!-- Smaller chart when showCountry -->
      <div v-if="showCountry" :id="chartId"
        style="top: 0; height: 95%; width: 95%; max-height: 500px; max-width:700px; position: absolute; margin-top: 30px">
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */

import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import { countryToAlpha3 } from "country-to-iso";
import { ref, defineProps, onMounted } from "vue";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  chartData: {
    type: Object,
    required: true,
  },
  showCountry: {
    type: String,
    required: false,
  }
});

const chartId = ref(props.id);
const hasData = ref(false);
let highlightOperationCountry = true;


const createChart = (chartData) => {
  let chart = am4core.create(props.id, am4charts.XYChart)

  chart.data = chartData;

  let title = chart.titles.create();
  title.text = "Opportunity vs Risk";
  title.fontSize = 20;
  title.marginBottom = 20;

  if (props.id === 'opvsglorisk') {
    title.paddingRight = 100;
  } else {
    title.paddingRight = 200;
  }

  title.align = "center";

  let xAxis = chart.xAxes.push(new am4charts.ValueAxis());
  xAxis.title.text = "Risk";
  xAxis.min = 2;
  xAxis.max = 5;
  xAxis.strictMinMax = true;
  xAxis.renderer.labels.template.adapter.add("text", function (text, target) {
    let value = parseFloat(text);
    if (Number.isInteger(value)) {
      return value.toFixed(1);
    }
    return "";
  });
  xAxis.renderer.minGridDistance = 20
  xAxis.renderer.grid.template.adapter.add("visible", function (visible, target) {
    let value = target.dataItem.value;
    return Number.isInteger(value);
  });

  let yAxis = chart.yAxes.push(new am4charts.ValueAxis());
  yAxis.title.text = "Opportunity";
  yAxis.min = 1.5;
  yAxis.max = 4.5;
  yAxis.strictMinMax = true;
  yAxis.renderer.labels.template.adapter.add("text", function (text, target) {
    let value = parseFloat(text);
    return value.toFixed(1);
  });

  let series = chart.series.push(new am4charts.LineSeries());
  series.dataFields.valueX = "overallcountryrisk";
  series.dataFields.valueY = "opport";
  series.strokeOpacity = 0;
  series.fillOpacity = 0;

  chart.events.on("ready", function () {
    chart.series.each(function (series) {
      series.bullets.each(function (bullet) {
        bullet.clones.each(function (item) {
          if (item.dataItem && item.dataItem.valueY) {
            item.zIndex = Math.round(Math.random() * 1000);
          }
        })
      })
    })
  });

  let bullet = series.bullets.push(new am4charts.CircleBullet());
  bullet.circle.radius = 6;
  bullet.propertyFields.fill = "color";

  // Dynamically change stroke color
  bullet.adapter.add("stroke", function (stroke, target) {
    if (target.dataItem && target.dataItem.dataContext.isoperation === 1 && highlightOperationCountry && !props.showCountry) {
      return am4core.color("#cc0000"); // Red stroke for active bullets
    }
    return am4core.color("#ffffff"); // White stroke for inactive bullets
  });

  // Dynamically change bullet zIndex
  bullet.adapter.add("zIndex", function (zIndex, target) {
    const dataItem = target.dataItem.dataContext;
    if (dataItem.isoperation === 1) {

      return 1000; // Higher zIndex for operation country
    } if (dataItem.country_name == props.showCountry) {

      return 1001; // Higher zIndex for active bullets
    }
    return 1; // Lower zIndex for other bullets
  });

  let labelBullet = series.bullets.push(new am4charts.LabelBullet());
  labelBullet.label.fill = am4core.color("#000");
  labelBullet.label.fontSize = 12;
  labelBullet.label.dx = 10;

  bullet.tooltipText = "{country_name}";

  chart.cursor = new am4charts.XYCursor();
  chart.cursor.lineX.disabled = true;
  chart.cursor.lineY.disabled = true;
  chart.cursor.behavior = "zoomXY"

  let legend = ''

  let selectedCountrysRegion = ''

  const regionColors = {
    "Asia & Oceania": "#e74c3c",
    "Europe": "#4287f5",
    "North Africa & Middle East": "#6d0c70",
    "North America": "#3ce7e7",
    "South & Central America": "#067d4d",
    "Sub-Saharan Africa": "#b69259",
    "Operation Country": "#bdc3c7"
  };

  if (!props.showCountry) {
    legend = new am4charts.Legend();
    legend.position = "right"; // <-- Position legend to the right
    legend.align = "left";     // Align legend to the left within its container
    chart.legend = legend;

    legend.data = Object.keys(regionColors).map((region) => ({
      name: region,
      regionName: region,
      fill: regionColors[region],
    }));

    chart.events.on("ready", () => {
      legend.itemContainers.each((item) => {
        item.isActive = item.dataItem.dataContext.regionName !== "Operation Country";
      });
      legend.invalidateData();
    });

  } else {

    for (let i = 0; i < props.chartData.length; i++) {
      if (countryToAlpha3(props.showCountry) == props.chartData[i].country_id) {
        selectedCountrysRegion = props.chartData[i].region_name
      }
    }

    let legend = new am4charts.Legend();

    legend.position = "right"; // <-- Position legend to the right
    legend.align = "left";     // Align legend to the left within its container
    chart.legend = legend;
    legend.useDefaultMarker = true;
    legend.data = [
      {
        name: selectedCountrysRegion,
        fill: am4core.color("#FF0000"),
      },
      {
        name: props.showCountry,
        fill: am4core.color("#4287f5"),
      }
    ];

    legend.itemContainers.template.clickable = false;
    legend.itemContainers.template.focusable = false;
    legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
    legend.itemContainers.template.events.disableType("hit"); // Prevent any click events

    var marker = chart.legend.markers.template.children.getIndex(0);

    marker.cornerRadius(12, 12, 12, 12);


  }



  if (!props.showCountry) {
    var marker = chart.legend.markers.template.children.getIndex(0);



    marker.cornerRadius(12, 12, 12, 12);

    marker.adapter.add("fill", (value, target) => {
      if (!target.dataItem) {
        return value;
      }
      var settings = target.dataItem.dataContext;
      // console.log(settings)
      return settings.fill
    })

    marker.adapter.add("stroke", function (value, target) {

      if (!target.dataItem) {
        return value;
      }
      var settings = target.dataItem.dataContext;

      if (settings.name == "Operation Country") {
        return am4core.color("red")
      } else {
        return value
      }
    })

    marker.adapter.add("strokeWidth", function (value, target) {

      if (!target.dataItem) {
        return value;
      }
      var settings = target.dataItem.dataContext;

      if (settings.name == "Operation Country") {
        return 2
      } else {
        return value
      }
    })

    marker.adapter.add("strokeOpacity", function (value, target) {

      if (!target.dataItem) {
        return value;
      }
      var settings = target.dataItem.dataContext;

      if (settings.name == "Operation Country") {
        return 1
      } else {
        return 0
      }
    })

  }

  let selectedRegions = { "Operation Country": false };


  chartData.forEach((dataItem) => {
    if (dataItem.isActive === 1) {
      dataItem.color = regionColors[dataItem.region_name]
    } else {
      dataItem.color = "#bdc3c7"
    }
  });


  if (props.showCountry) {
    let noDataLabel = chart.chartContainer.createChild(am4core.Label);
    noDataLabel.align = "center";
    noDataLabel.valign = "bottom";
    noDataLabel.fill = am4core.color("#ff0000")
    noDataLabel.dy = 15

    chartData.forEach((dataItem) => {
      if (countryToAlpha3(props.showCountry) == dataItem.country_id) {
        dataItem.color = "#4287f5"
        console.log("aeiou")
        console.log(dataItem)
        if (dataItem.opport == 0 || dataItem.opport == null) {
          noDataLabel.text = "Warning: no Opportunity Data for " + dataItem.country_name
        }
      } else if (selectedCountrysRegion == dataItem.region_name && countryToAlpha3(props.showCountry) != dataItem.country_id) {
        dataItem.color = "#ff0000"
      } else {
        dataItem.color = "#bdc3c7"
      }
    })
  }
  chart.exporting.menu = new am4core.ExportMenu();
  chart.exporting.menu.align = "left";
  chart.exporting.filePrefix = 'RiskVsOpportunity'
  // chart.exporting.filePrefix = this.$route.params.element + this.currentChartField + "Map";
  chart.exporting.menu.verticalAlign = "top";

  bullet.events.on("over", function () {
    bullet.zIndex = 1001;
  })

  // legend.data = Object.keys(regionColors).map((region) => ({
  //   name: region,
  //   regionName: region,
  //   fill: regionColors[region], 
  // }));

  if (!props.showCountry) {


    // setTimeout(() => {
    //   legend.itemContainers.each((item) => {
    //     item.isActive = item.dataItem.dataContext.regionName !== "Operation Country";
    //   });
    // }, 0);

    legend.itemContainers.template.events.on("hit", (ev) => {
      const regionClicked = ev.target.dataItem.name;
      if (regionClicked === "Operation Country") {
        console.log(selectedRegions)
        highlightOperationCountry = !highlightOperationCountry
        if (highlightOperationCountry) {
          chartData.forEach((dataItem) => {
            if (dataItem.isoperation === 1) {
              dataItem.isActive = selectedRegions[regionClicked] ? 0 : 1;
              dataItem.color = regionColors[dataItem.region_name]


            }
          });
        } else {
          chartData.forEach((dataItem) => {
            dataItem.isActive = selectedRegions[regionClicked] ? 0 : 1;
            if (!selectedRegions[dataItem.region_name]) {
              dataItem.color = "#bdc3c7"
            }
          })
        }
        series.bullets.each((bullet) => {
          const dataItem = bullet.dataItem;
          const data = dataItem?.dataContext;

          if (data?.isoperation === 1) {
            bullet.zIndex = 1000;
          } else {
            bullet.zIndex = 1;
          }

          bullet.invalidate(); // Force redraw
        });
        selectedRegions[regionClicked] = !selectedRegions[regionClicked]
        chart.invalidateRawData()
      } else {
        if (selectedRegions[regionClicked]) {
          delete selectedRegions[regionClicked];
        } else {
          selectedRegions[regionClicked] = true;
        }
        chartData.forEach((dataItem) => {
          if (selectedRegions[dataItem.region_name] || (!selectedRegions["Operation Country"] && dataItem.isoperation === 1)) {
            dataItem.isactive = 1;
            dataItem.color = regionColors[dataItem.region_name] || "#bdc3c7";
          } else {
            dataItem.isactive = 0;
            dataItem.color = "#bdc3c7";
          }
        });
        console.log("pain")
        legend.data.forEach((item) => {
          const region = item.regionName;
          console.log(region)
          if (region === "Operation Country") {
            item.fill = regionColors[region];
          } else {
            // item.fill = selectedRegions[region] ? regionColors[region] : "#bdc3c7";
            item.fill = regionColors[region];
            console.log(item.fill)
          }

        });
      }

      chart.invalidateData();
      legend.invalidateData();
    });
  }


  let topLeftLabel = chart.plotContainer.createChild(am4core.Label);
  topLeftLabel.html = "Low Risk<br>High Opportunity"
  topLeftLabel.fill = am4core.color("#ffffff");
  topLeftLabel.background = new am4core.RoundedRectangle();
  topLeftLabel.background.fill = am4core.color("#28a745");
  // topLeftLabel.background.stroke = am4core.color("#000000");
  topLeftLabel.padding(5, 10, 5, 10);
  topLeftLabel.align = "left";
  topLeftLabel.valign = "top";
  topLeftLabel.marginLeft = 10;
  topLeftLabel.marginTop = 10;

  let bottomRightLabel = chart.plotContainer.createChild(am4core.Label);
  bottomRightLabel.html = "High Risk<br>Low Opportunity";
  bottomRightLabel.fill = am4core.color("#ffffff");
  bottomRightLabel.background = new am4core.RoundedRectangle();
  bottomRightLabel.background.fill = am4core.color("#dc3545");
  // bottomRightLabel.background.stroke = am4core.color("#000000"); 
  bottomRightLabel.padding(5, 10, 5, 10);
  bottomRightLabel.align = "right";
  bottomRightLabel.valign = "bottom";
  bottomRightLabel.marginRight = 10;
  bottomRightLabel.marginBottom = 10;

}

onMounted(() => {
  if ((props.chartData && Object.keys(props.chartData).length > 0)) {
    hasData.value = true;
    createChart(props.chartData);
  } else {
    console.log("Her is NOT ok")
    hasData.value = false;
  }
});


</script>

<style scoped>
.no-data-message {
  font-size: 16px;
  text-align: center;
  color: #999;
  padding: 20px;
}
</style>